import React, { useEffect, useRef, useState } from 'react';
import * as echarts from 'echarts';
import './ElectricityAccessChart.scss';
import { API_BASE } from '../../config/environments/api';
import CleanCookingPage from '../../pages/CleanCookingPage/CleanCookingPage';

// Suppress ECharts legend warnings
const originalWarn = console.warn;
console.warn = (...args) => {
  const message = args.join(' ');
  if (message.includes('series not exists') || message.includes('Legend data should be same with series name')) {
    return;
  }
  originalWarn.apply(console, args);
};

// Single color for all countries
const BUBBLE_COLOR = '#D48019';

const ElectricityAccessChart = () => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const [chartData, setChartData] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(2022);

  // Fetch bubble chart data from API
  const fetchBubbleData = async (year, isInitialLoad = false) => {
    try {
      // Only show loading on initial mount
      if (isInitialLoad) {
        setInitialLoading(true);
      }
      
      const response = await fetch(`${API_BASE}/electricity-access-bubble?year=${year}`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          // Transform API data to chart format
          const transformedData = data.data.map(item => ({
            name: item.name,
            rural: item.x, // X-axis: Rural (Population without access to electricity - Rural)
            urban: item.y, // Y-axis: Urban (Population without access to electricity - Urban)
            unelectrified: item.value, // Bubble size: average of rural and urban
            region: 'Africa' // All data is from Africa
          }));
        
          setChartData(transformedData);
          setError(null);
        } else {
          throw new Error('Invalid data format from API');
        }
      } else {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      setError(error.message);
      // Don't clear chartData on error - keep previous data visible
    } finally {
      // Only set loading to false on initial load
      if (isInitialLoad) {
        setInitialLoading(false);
      }
    }
  };

  // Fetch available years and load initial data
  useEffect(() => {
    const fetchYears = async () => {
      try {
        const response = await fetch(`${API_BASE}/years`);
        
        if (response.ok) {
          const data = await response.json(); 
          if (data.years && data.years.length > 0) {
            const uniqueYears = [...new Set(data.years.map(year => Number(year)))].sort((a, b) => b - a);
            setAvailableYears(uniqueYears);
            if (uniqueYears.length > 0) {
              const firstYear = uniqueYears[0];
              setSelectedYear(firstYear);
              // Load initial data after setting year
              fetchBubbleData(firstYear, true);
            }
          }
        }
      } catch (error) {
      }
    };

    fetchYears();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper function to get chart option
  const getChartOption = () => {
    return {
      animation: true,
      animationDuration: 500,
      animationEasing: 'cubicOut',
      title: {
        text: null
      },
      tooltip: {
        trigger: 'item',
        textStyle: {
          fontFamily: "'Poppins', sans-serif"
        },
        formatter: function(params) {
          try {
            // Data structure: {name, value: [rural, urban, average]}
            const rural = params.data.value[0] || 0;
            const urban = params.data.value[1] || 0;
            const average = params.data.value[2] || 0;
            const name = params.data.name || 'Unknown';
            
            return `
              <div style="padding: 5px; background: white; border-radius: 8px; font-family: 'Poppins', sans-serif;">
                <div style="font-size: 14px; font-weight: 500; color: #333; margin-bottom: 8px; font-family: 'Poppins', sans-serif;">
                  ${name}
                </div>
                <div style="color: #666; margin-bottom: 4px; font-family: 'Poppins', sans-serif;">
                  <span style="color: #e74c3c;">●</span> Rural (without access): <span style="font-weight: 600;color:#333">${rural.toFixed(1)}M</span>
                </div>
                <div style="color: #666; margin-bottom: 4px; font-family: 'Poppins', sans-serif;">
                  <span style="color: #3498db;">●</span> Urban (without access): <span style="font-weight: 600;color:#333">${urban.toFixed(1)}M</span>
                </div>
                <div style="color: #666; border-top: 1px solid #eee; padding-top: 4px; margin-top: 4px; font-family: 'Poppins', sans-serif;">
                  Average: <span style="font-weight: 600;color:#333">${average.toFixed(1)}M</span>
                </div>
              </div>
            `;
          } catch (error) {
            return `<div>Error loading tooltip</div>`;
          }
        }
      },
      legend: {
        show: false, // Hide legend to avoid ECharts warnings about series name mismatch
        type: 'scroll',
        orient: 'horizontal',
        bottom: 20,
        left: 'center',
        itemWidth: 18,
        itemHeight: 18,
        textStyle: {
          fontSize: 12,
          color: '#666666',
          fontWeight: '500',
          fontFamily: "'Poppins', sans-serif"
        }
      },
      grid: {
        left: '8%',
        right: '5%',
        top: '8%',
        bottom: '12%',
        containLabel: true
      },
      xAxis: {
        type: 'value',
        name: '',
        nameLocation: 'middle',
        nameGap: 0,
        nameTextStyle: {
          fontSize: 12,
          color: '#666',
          fontFamily: "'Poppins', sans-serif"
        },
        min: 0,
        axisLabel: {
          formatter: '{value}M',
          fontFamily: "'Poppins', sans-serif"
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: '#f0f0f0'
          }
        }
      },
      yAxis: {
        type: 'value',
        name: 'Without access to electricity - Urban (Million)',
        nameLocation: 'middle',
        nameGap: 50,
        nameTextStyle: {
          fontSize: 12,
          color: '#666',
          fontFamily: "'Poppins', sans-serif"
        },
        min: 0,
        axisLabel: {
          formatter: '{value}M',
          fontFamily: "'Poppins', sans-serif"
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: '#f0f0f0'
          }
        }
      },
      series: [
        {
          name: 'Countries',
          type: 'scatter',
          data: chartData.length > 0 ? chartData.map((item, index) => {
            const dataPoint = {
              name: item.name,
              value: [item.rural, item.urban, item.unelectrified],
              itemStyle: {
                color: BUBBLE_COLOR
              }
            };
            return dataPoint;
          }) : [],
          symbolSize: function (data) {
            try {
              let average = 0;
              
              // Check different possible data structures
              if (data && data.value && Array.isArray(data.value)) {
                // Structure: {name, value: [rural, urban, average]}
                average = data.value[2] || 0;
              } else if (data && Array.isArray(data)) {
                // Structure: [rural, urban, average, name, color]
                average = data[2] || 0;
              } else if (data && typeof data === 'number') {
                // Structure: just a number
                average = data;
              } else {
                average = 0;
              }
              
              // Custom size groups based on data values
              let size = 5; // Default minimum size
              
              if (average <= 1) {
                size = 10;   // 1M or less
              } else if (average <= 5) {
                size = 20;  // 1M - 5M
              } else if (average <= 10) {
                size = 30;  // 5M - 10M
              } else if (average <= 15) {
                size = 40;  // 10M - 15M
              } else if (average <= 20) {
                size = 50;  // 15M - 20M
              } else if (average <= 25) {
                size = 68;  // 20M - 25M
              } else if (average <= 30) {
                size = 80;  // 25M - 30M
              } else if (average <= 35) {
                size = 85;  // 30M - 35M
              } else {
                size = 90;  // 35M and above
              }
              return size;
            } catch (error) {
              return 15; // Default size if error
            }
          },
          itemStyle: {
            opacity: 0.8
          },
          emphasis: {
            itemStyle: {
              opacity: 1,
              borderWidth: 3,
              borderColor: '#FFA500',
              shadowBlur: 10,
              shadowColor: 'rgba(255, 215, 0, 0.5)'
            }
          }
        }
      ],
      graphic: chartData.length === 0 ? {
        type: 'text',
        left: 'center',
        top: 'middle',
        style: {
          text: 'No data available for this year',
          fontSize: 16,
          fill: '#666'
        }
      } : null,
      media: [
        {
          query: { maxWidth: 1024 },
          option: {
            grid: {
              left: '8%',
              right: '8%',
              top: '8%',
            },
            title: {
              text: null
            },
          }
        },
        {
          query: { maxWidth: 768 },
          option: {
            grid: {
              left: '10%',
              right: '10%',
              top: '15%'
            },
            title: {
              text: null
            },
          }
        }
      ]
    };
  };

  // Update data when year changes (without showing loader)
  useEffect(() => {
    // Skip if this is the initial load (handled by fetchYears useEffect)
    // or if selectedYear hasn't been set yet
    if (initialLoading || !selectedYear || availableYears.length === 0) {
      return;
    }
    fetchBubbleData(selectedYear, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  // Initialize chart instance only once - wait for loading to finish and chart container to be rendered
  useEffect(() => {
    // Only initialize after initial loading is done and chart container is available
    if (!initialLoading && chartRef.current && !chartInstanceRef.current) {
      try {
        chartInstanceRef.current = echarts.init(chartRef.current);
        
        // If we already have data, render it immediately
        if (chartData.length > 0) {
          const option = getChartOption();
          chartInstanceRef.current.setOption(option, {
            notMerge: false,
            lazyUpdate: false
          });
        }
        
        // Handle window resize
        const handleResize = () => {
          if (chartInstanceRef.current) {
            chartInstanceRef.current.resize();
          }
        };

        window.addEventListener('resize', handleResize);

        return () => {
          window.removeEventListener('resize', handleResize);
          if (chartInstanceRef.current) {
            chartInstanceRef.current.dispose();
            chartInstanceRef.current = null;
          }
        };
      } catch (error) {
      }
    }
  }, [initialLoading, chartData]);

  // Update chart data smoothly when chartData changes
  useEffect(() => {
    // Only update if chart instance exists
    if (!chartInstanceRef.current) {
      return;
    }
    
    try {
      const option = getChartOption();
      
      // Update chart with smooth transition - notMerge: false allows smooth updates
      chartInstanceRef.current.setOption(option, {
        notMerge: false,
        lazyUpdate: false
      });
    } catch (error) {
    }
  }, [chartData, selectedYear]);
  useEffect(() => {
    // Only update if chart instance exists
    if (!chartInstanceRef.current) {
      return;
    }
    
    try {
      const option = getChartOption();
      
      // Update chart with smooth transition - notMerge: false allows smooth updates
      chartInstanceRef.current.setOption(option, {
        notMerge: false,
        lazyUpdate: false
      });
    } catch (error) {
    }
  }, [chartData, selectedYear]);

  return (
    <>
    <div className="energy-access-chart-section">
      <div className='container'>
      <div className='section-header'>
        <h2 className="section-title">Energy Access Chart <span style={{color:'#D48019'}}>Africa</span></h2>
      </div>
      <div className="charts-wrapper">
        <section className="clean-cooking-section">
          <CleanCookingPage />
        </section>
      <section className="electricity-access-chart-section">
            <div className="section-header">
              {/* <h2 className="section-title">Energy Access Chart <span>Africa</span></h2> */}
              <p className="section-subtitle">
                Population without access to electricity ({chartData.length} countries)
              </p>
              <div className="year-selector">
                <label htmlFor="year-select">Year:</label>
                <select 
                  id="year-select"
                  value={selectedYear} 
                  onChange={(e) => {
                    setSelectedYear(parseInt(e.target.value));
                  }}
                  className="year-dropdown"
                >
                  {availableYears.map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="chart-container">
              {initialLoading ? (
                <div className="loading-container">
                  <img 
                    src="/spinner/oval-spinner.gif" 
                    alt="Loading..." 
                    className="chart-loader-spinner"
                  />
                </div>
              ) : (
                <>
                  {error && (
                    <div className="error-container">
                      <p>Error loading chart data: {error}</p>
                    </div>
                  )}
                  <div ref={chartRef} className="chart" style={{ width: '100%', height: '400px' }}></div>
                </>
              )}
            </div>
            <div className="chart-legend">
              <div className="legend-note">
                <p>Without access to electricity - Rural (Million)</p>
                <p>Bubble size = Average of rural and urban population without access to electricity</p>
                <br />
                {/* <small>Total countries: {chartData.length} | Country names shown in legend</small> */}
              </div>
              {error && (
                <div className="error-note">
                  <small>Note: Error loading data</small>
                </div>
              )}
            </div>
        </section>
      </div>
      </div>
      </div>
    </>
  );
};

export default ElectricityAccessChart;

